
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useTenant } from './TenantContext';
import { TerminologySettings } from '../types';
import { getGlobalTerminology } from '../services/dataService';

const DEFAULT_TERMS: TerminologySettings = {
  studio: 'Studio',
  artist: 'Artist',
  artists: 'Artists',
  client: 'Client',
  clients: 'Clients',
  service: 'Service',
  services: 'Services',
  booking: 'Session',
  bookings: 'Sessions',
  project: 'Project',
  projects: 'Projects'
};

const TerminologyContext = createContext<TerminologySettings>(DEFAULT_TERMS);

export const useTerminology = () => useContext(TerminologyContext);

export const TerminologyProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { tenant } = useTenant();
  const [globalTerms, setGlobalTerms] = useState<TerminologySettings>(DEFAULT_TERMS);

  useEffect(() => {
      // Fetch global terms from settings/terminology to overlay on defaults
      const fetchGlobal = async () => {
          try {
              const terms = await getGlobalTerminology();
              if (terms) {
                  setGlobalTerms({ ...DEFAULT_TERMS, ...terms });
              }
          } catch(e) {
              console.warn("Failed to fetch global terminology", e);
          }
      };
      fetchGlobal();
  }, []);

  const terms = useMemo(() => {
    // Priority 1: Tenant Config
    if (tenant?.config?.terminology) {
      return { ...DEFAULT_TERMS, ...globalTerms, ...tenant.config.terminology };
    }
    // Priority 2: Global DB Config (Managed by Super Admin)
    return { ...DEFAULT_TERMS, ...globalTerms };
  }, [tenant, globalTerms]);

  return (
    <TerminologyContext.Provider value={terms}>
      {children}
    </TerminologyContext.Provider>
  );
};
